import { useState } from "react";
import { Mail, Sparkles, Check, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { subscribeToDigest } from "@/lib/api";

export default function WeeklyDigestSubscribe({ variant = "default" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      setStatus("error");
      setMessage("Please enter a valid email address");
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const result = await subscribeToDigest(email);
      setStatus("success");
      setMessage(result.message || "You're subscribed! Check your inbox.");
      setEmail("");
      
      // Reset after 5 seconds
      setTimeout(() => {
        setStatus("idle");
        setMessage("");
      }, 5000);
    } catch (err) {
      setStatus("error");
      setMessage(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (variant === "compact") {
    return (
      <div className="rounded-xl p-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-4 h-4 text-amber-400/70" />
          <h3 className="text-sm font-medium text-white/80">Weekly Digest</h3>
        </div>
        
        <p className="text-white/40 text-xs mb-3">
          Get upcoming celestial events delivered to your inbox every week.
        </p>

        <form onSubmit={handleSubmit} className="space-y-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            disabled={status === "loading" || status === "success"}
            className="w-full px-3 py-2 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-amber-500/50 disabled:opacity-50"
            data-testid="digest-email-input"
          />
          
          <button
            type="submit"
            disabled={status === "loading" || status === "success"}
            className="w-full px-3 py-2 rounded-lg text-sm font-medium bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="digest-subscribe-btn"
          >
            {status === "loading" && <Loader2 className="w-4 h-4 animate-spin" />}
            {status === "success" && <Check className="w-4 h-4" />}
            {status === "loading" ? "Subscribing..." : status === "success" ? "Subscribed!" : "Subscribe"}
          </button>
        </form>

        <AnimatePresence>
          {message && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`text-xs mt-2 ${status === "error" ? "text-red-400" : "text-green-400"}`}
            >
              {message}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Default full variant
  return (
    <div
      className="rounded-2xl p-6 sm:p-8 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, rgba(251, 146, 60, 0.08) 0%, rgba(59, 130, 246, 0.05) 100%)",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
      data-testid="weekly-digest-subscribe"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(251, 146, 60, 0.4) 0%, transparent 70%)",
            transform: "translate(30%, -30%)",
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-start gap-4 mb-5">
          <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl font-light text-white/90 mb-1">
              Weekly Celestial Digest
            </h3>
            <p className="text-white/50 text-sm">
              Never miss a significant cosmic event
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="text-white/60 text-sm leading-relaxed mb-6">
          Subscribe to receive a beautifully crafted summary of the week's upcoming astrological events — 
          retrogrades, sign changes, moon phases, and eclipses — delivered directly to your inbox every Monday.
        </p>

        {/* Features */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {[
            "Planetary retrogrades",
            "Sign ingresses",
            "New & full moons",
            "Eclipse alerts",
          ].map((feature) => (
            <div key={feature} className="flex items-center gap-2 text-white/50 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400/50" />
              {feature}
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              disabled={status === "loading" || status === "success"}
              className="flex-1 px-4 py-3 rounded-xl text-sm bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-amber-500/50 disabled:opacity-50"
              data-testid="digest-email-input-full"
            />
            
            <button
              type="submit"
              disabled={status === "loading" || status === "success"}
              className="px-6 py-3 rounded-xl text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-400 hover:to-orange-400 transition-all disabled:opacity-50 flex items-center justify-center gap-2 min-w-[140px]"
              data-testid="digest-subscribe-btn-full"
            >
              {status === "loading" && <Loader2 className="w-4 h-4 animate-spin" />}
              {status === "success" && <Check className="w-4 h-4" />}
              {status === "loading" ? "Subscribing..." : status === "success" ? "Subscribed!" : "Subscribe"}
            </button>
          </div>

          <AnimatePresence>
            {message && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`text-sm ${status === "error" ? "text-red-400" : "text-green-400"}`}
              >
                {message}
              </motion.p>
            )}
          </AnimatePresence>
        </form>

        {/* Privacy note */}
        <p className="text-white/25 text-[11px] mt-4">
          We respect your privacy. Unsubscribe anytime with one click.
        </p>
      </div>
    </div>
  );
}
